import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import {
  OPERATORS, EMPTY_RULE_DEFINITION, DEFAULT_SCORING, getStatusInfo,
} from '../utils/ruleTypes';
import { RuleTestPanel } from './RuleTestPanel';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/Rules';

const OPERATORS_BY_DATA_TYPE = {
  String: ['Equals', 'NotEquals', 'Contains', 'StartsWith', 'EndsWith', 'IsNull', 'IsNotNull'],
  Number: ['Equals', 'NotEquals', 'GreaterThan', 'GreaterThanOrEqual', 'LessThan', 'LessThanOrEqual', 'IsNull', 'IsNotNull'],
  Decimal: ['Equals', 'NotEquals', 'GreaterThan', 'GreaterThanOrEqual', 'LessThan', 'LessThanOrEqual', 'IsNull', 'IsNotNull'],
  Boolean: ['Equals', 'NotEquals', 'IsNull', 'IsNotNull'],
  Date: ['Equals', 'NotEquals', 'GreaterThan', 'GreaterThanOrEqual', 'LessThan', 'LessThanOrEqual', 'IsNull', 'IsNotNull'],
};

function getOperatorsForDataType(dataType) {
  const allowed = OPERATORS_BY_DATA_TYPE[dataType];
  if (!allowed) return OPERATORS;
  return OPERATORS.filter((op) => allowed.includes(op.value));
}

// Yaşam döngüsü geçişleri (durum → izin verilen aksiyonlar)
const STATUS_ACTIONS = {
  0: [
    { action: 'mark-testing', label: 'Teste Al', icon: 'check' },
    { action: 'submit', label: 'Onaya Gönder', icon: 'check' },
  ],
  1: [{ action: 'submit', label: 'Onaya Gönder', icon: 'check' }],
  2: [
    { action: 'approve', label: 'Onayla', icon: 'check' },
    { action: 'reject', label: 'Reddet', icon: 'x' },
  ],
  3: [{ action: 'archive', label: 'Arşivle', icon: 'box' }],
  4: [],
};

const ACTION_TITLES = {
  submit: 'Onaya Gönder',
  approve: 'Kuralı Onayla',
  reject: 'Onayı Reddet',
  archive: 'Kuralı Arşivle',
  'mark-testing': 'Teste Al',
};

function createEmptyForm(workflowName = '') {
  return {
    workflowName,
    workflowDescription: '',
    workflowIsActive: true,
    ruleName: '',
    description: '',
    priority: 1,
    category: '',
    categoryId: '',
    targetEntity: '',
    isActive: true,
    actionTemplate: '',
    suppressRuleIds: '',
    changeNote: '',
    ...DEFAULT_SCORING,
  };
}

function mapRuleToForm(rule) {
  return {
    workflowName: rule.workflowName || '',
    workflowDescription: rule.workflowDescription || '',
    workflowIsActive: rule.workflowIsActive ?? true,
    ruleName: rule.ruleName || '',
    description: rule.description || '',
    priority: rule.priority || 1,
    category: rule.category || '',
    categoryId: rule.categoryId ?? '',
    targetEntity: rule.targetEntity || '',
    isActive: rule.isActive ?? true,
    actionTemplate: rule.actionTemplate || '',
    suppressRuleIds: rule.suppressRuleIds || '',
    changeNote: '',
    impactScore: rule.impactScore ?? 5,
    easeScore: rule.easeScore ?? 5,
    urgencyScore: rule.urgencyScore ?? 5,
    relationScore: rule.relationScore ?? 5,
    impactWeight: rule.impactWeight ?? 0.25,
    easeWeight: rule.easeWeight ?? 0.25,
    urgencyWeight: rule.urgencyWeight ?? 0.25,
    relationWeight: rule.relationWeight ?? 0.25,
  };
}

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

export function RuleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni İş Kuralı' : 'İş Kuralı Detayı', 'İş kuralı kartı, koşullar, skorlama ve yaşam döngüsü.');

  const tenantId = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}')?.tenantId || '', []);

  const [form, setForm] = useState(createEmptyForm());
  const [definition, setDefinition] = useState(EMPTY_RULE_DEFINITION);
  const [status, setStatus] = useState(0);
  const [version, setVersion] = useState(1);

  const [categories, setCategories] = useState([]);
  const [workflowDefinitions, setWorkflowDefinitions] = useState([]);
  const [workflowInputs, setWorkflowInputs] = useState([]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Yaşam döngüsü not modalı
  const [noteModal, setNoteModal] = useState(null); // { action }
  const [noteText, setNoteText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Versiyon geçmişi
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Test paneli (mevcut overlay bileşeni)
  const [showTest, setShowTest] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const setF = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // ── Destek verileri: kategoriler + workflow tanımları ──
  useEffect(() => {
    fetch('/api/RuleCategories', { headers: authHeaders() })
      .then((r) => r.json())
      .then((json) => setCategories((json.data ?? json ?? []).filter((c) => c.isActive)))
      .catch(() => {});
    fetch('/api/Rules/workflows', { headers: authHeaders() })
      .then((r) => r.json())
      .then((json) => setWorkflowDefinitions(Array.isArray(json) ? json : []))
      .catch(() => {});
  }, []);

  const applyRule = useCallback((r) => {
    setForm(mapRuleToForm(r));
    setStatus(Number(pick(r, 'status', 'Status') ?? 0));
    setVersion(pick(r, 'version', 'Version') ?? 1);
    try {
      setDefinition(JSON.parse(pick(r, 'ruleJson', 'RuleJson')) || EMPTY_RULE_DEFINITION);
    } catch {
      setDefinition(EMPTY_RULE_DEFINITION);
    }
    setJsonError('');
  }, []);

  // Tek kayıt yükleme: önce location.state.row; yoksa OData ile $filter=Id eq id.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      let r = location.state?.row;
      const fromState = !!r;
      if (!r) {
        const url = `${API_BASE}?$filter=${encodeURIComponent(`Id eq ${id}`)}&$top=1`;
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Kural alınamadı (${res.status})`);
        const json = await res.json();
        const items = json.value ?? json.data ?? json ?? [];
        r = Array.isArray(items) ? items[0] : items;
      }
      if (!r) throw new Error('Kural bulunamadı.');
      applyRule(r);
      const applyAudit = (rec) => setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
      if (fromState) {
        // Nav-state satırı liste OData $select'inden gelir → audit alanları eksik.
        // Formu anında doldur; audit'i tam kayıttan (OData) NON-BLOCKING çek.
        fetch(`${API_BASE}?$filter=${encodeURIComponent(`Id eq ${id}`)}&$top=1`, { headers: authHeaders() })
          .then((res) => (res.ok ? res.json() : null))
          .then((json) => { const items = json?.value ?? json?.data ?? json ?? []; const rec = Array.isArray(items) ? items[0] : items; if (rec) applyAudit(rec); })
          .catch(() => {});
      } else {
        applyAudit(r);
      }
    } catch (err) {
      showToast('error', err.message || 'Kural alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state, applyRule]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /rules/new'e kopya veriyle gelindiyse formu doldur (kullanıcı Kaydet diyene kadar kaydedilmez).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setDefinition(location.state.cloneDefinition || EMPTY_RULE_DEFINITION);
      setStatus(0);
      setVersion(1);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Workflow input alanları (koşul editöründe alan seçimi için).
  useEffect(() => {
    const workflowName = form.workflowName?.trim();
    if (!workflowName || workflowDefinitions.length === 0) { setWorkflowInputs([]); return; }
    const wd = workflowDefinitions.find((item) => item.name === workflowName);
    if (!wd?.id) { setWorkflowInputs([]); return; }
    let cancelled = false;
    fetch(`/api/workflows/${wd.id}/inputs`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        const items = json.data ?? json ?? [];
        setWorkflowInputs(Array.isArray(items) ? items.filter((x) => x.isActive) : []);
      })
      .catch(() => { if (!cancelled) setWorkflowInputs([]); });
    return () => { cancelled = true; };
  }, [form.workflowName, workflowDefinitions]);

  // Workflow seçilince açıklama/aktiflik tanımdan doldur.
  useEffect(() => {
    if (!form.workflowName || workflowDefinitions.length === 0) return;
    const def = workflowDefinitions.find((item) => item.name === form.workflowName);
    if (!def) return;
    const nextDescription = form.workflowDescription || def.description || '';
    const nextIsActive = def.isActive ?? form.workflowIsActive;
    if (nextDescription === form.workflowDescription && nextIsActive === form.workflowIsActive) return;
    setForm((current) => ({
      ...current,
      workflowDescription: current.workflowDescription || def.description || '',
      workflowIsActive: def.isActive ?? current.workflowIsActive,
    }));
  }, [form.workflowDescription, form.workflowIsActive, form.workflowName, workflowDefinitions]);

  // ── Koşullar ──
  const getInputDefinitionForField = (fieldName) => workflowInputs.find((item) => item.fieldName === fieldName);

  const addCondition = () => setDefinition((d) => ({
    ...d, conditions: [...d.conditions, { field: '', operator: 'Equals', value: '' }],
  }));
  const removeCondition = (i) => setDefinition((d) => ({ ...d, conditions: d.conditions.filter((_, idx) => idx !== i) }));
  const updateCondition = (i, key, val) => setDefinition((d) => {
    const conds = [...d.conditions];
    conds[i] = { ...conds[i], [key]: val };
    return { ...d, conditions: conds };
  });
  const updateConditionField = (i, fieldName) => {
    const inputDefinition = getInputDefinitionForField(fieldName);
    setDefinition((d) => {
      const conds = [...d.conditions];
      const nextCondition = { ...conds[i], field: fieldName };
      const allowedOperators = getOperatorsForDataType(inputDefinition?.dataTypeLabel);
      if (allowedOperators.length > 0 && !allowedOperators.some((op) => op.value === nextCondition.operator)) {
        nextCondition.operator = allowedOperators[0].value;
      }
      if (['IsNull', 'IsNotNull'].includes(nextCondition.operator)) nextCondition.value = '';
      conds[i] = nextCondition;
      return { ...d, conditions: conds };
    });
  };

  // ── Skorlama ──
  const weightSum = +form.impactWeight + +form.easeWeight + +form.urgencyWeight + +form.relationWeight;
  const weightOk = Math.abs(weightSum - 1.0) < 0.01;
  const computedScore = (
    +form.impactScore * +form.impactWeight
    + +form.easeScore * +form.easeWeight
    + +form.urgencyScore * +form.urgencyWeight
    + +form.relationScore * +form.relationWeight
  );

  // andNew: kaydet ama detaya/listeye gitme — formu sıfırlayıp yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.workflowName?.trim()) {
      setJsonError('Kural kaydetmek için workflow seçimi zorunludur.');
      showToast('error', 'Workflow seçimi zorunludur.');
      setActiveTab('general');
      return;
    }
    if (!form.ruleName?.trim()) {
      showToast('error', 'Kural adı zorunludur.');
      setActiveTab('general');
      return;
    }
    if (!weightOk) {
      setJsonError(`Ağırlıkların toplamı 1.0 olmalı (şu an: ${weightSum.toFixed(3)}).`);
      showToast('error', 'Ağırlıkların toplamı 1.0 olmalı.');
      return;
    }
    setJsonError('');
    setSaving(true);
    try {
      const payload = { ...form, ruleJson: JSON.stringify(definition) };
      const url = isNew ? `${API_BASE}/Add` : `${API_BASE}/Update`;
      const method = isNew ? 'POST' : 'PUT';
      if (!isNew) payload.id = id;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || `Kayıt başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Kural eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(createEmptyForm(form.workflowName));
          setDefinition(EMPTY_RULE_DEFINITION);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Kural eklendi.');
        setTimeout(() => navigate('/rules'), 400);
        return;
      }
      showToast('success', 'Kural güncellendi.');
      setTimeout(() => navigate('/rules'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut form + koşulları taşıyarak yeni-kayıt ekranını aç (hiçbir şey kaydedilmez).
  const handleClone = () => {
    navigate('/rules/new', { state: { clone: { ...form, changeNote: '' }, cloneDefinition: { ...definition } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Kural silindi.');
      setTimeout(() => navigate('/rules'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // ── Yaşam döngüsü geçişleri ──
  const openNoteModal = (action) => { setNoteModal({ action }); setNoteText(''); };
  const confirmAction = async () => {
    if (!noteModal || isNew) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/${noteModal.action}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: noteText }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || `${noteModal.action} işlemi başarısız.`);
      showToast('success', 'İşlem tamamlandı.');
      setNoteModal(null);
      load();
      if (activeTab === 'lifecycle') fetchHistory();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Versiyon geçmişi ──
  const fetchHistory = useCallback(async () => {
    if (isNew) { setHistory([]); return; }
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/history`, { headers: authHeaders() });
      const json = await res.json();
      setHistory(json.data ?? json ?? []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { if (!isNew && activeTab === 'lifecycle') fetchHistory(); }, [isNew, activeTab, fetchHistory]);

  const si = getStatusInfo(status);
  const statusBadge = !isNew && (
    <StatusBadge tone={status === 3 ? 'ok' : status === 4 ? 'neutral' : 'wait'}>{si.label}</StatusBadge>
  );

  const lifecycleActions = STATUS_ACTIONS[status] ?? [];

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'list' },
    { key: 'lifecycle', label: 'Yaşam Döngüsü', icon: 'shield', disabled: isNew, title: isNew ? 'Önce kuralı kaydedin' : undefined },
  ];

  const actions = (
    <>
      {!isNew && (
        <button className="dps-btn" onClick={() => setShowTest(true)} disabled={saving}>
          <Icon name="check" size={16} /> Test
        </button>
      )}
      {!isNew && status !== 3 && canDelete && (
        <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}>
          <Icon name="x" size={16} /> Sil
        </button>
      )}
      <button className="dps-btn ghost" onClick={() => navigate('/rules')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kuralı kopyalayıp yeni kural aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  const categoryOptions = useMemo(() => ([
    { value: '', label: 'Seçiniz' },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ]), [categories]);

  const workflowOptions = useMemo(() => ([
    { value: '', label: 'Workflow seçiniz' },
    ...[...new Set(workflowDefinitions.map((item) => item.name).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'tr', { sensitivity: 'base' }))
      .map((name) => ({ value: name, label: name })),
  ]), [workflowDefinitions]);

  return (
    <DetailShell
      crumb={[{ label: 'İş Kuralı Yönetimi', to: '/rules' }, { label: isNew ? 'Yeni İş Kuralı' : 'İş Kuralı Detayı', current: true }]}
      status={statusBadge}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'BusinessRule', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section
            icon="list"
            title="Kural Bilgileri"
            subtitle="Workflow, isim, öncelik ve kategori"
            note="Workflow tanımları ve input alanları ayrı Workflow Yönetimi sayfasından tanımlanır. Burada mevcut workflow seçip ona bağlı kuralı düzenlersiniz."
          >
            <Grid cols={2}>
              <FieldCustom label="Workflow Adı" req invalid={tried && !form.workflowName?.trim()}>
                <div className="dps-ff-input">
                  <select value={form.workflowName} onChange={(e) => { setF('workflowName', e.target.value); setJsonError(''); }}>
                    {workflowOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
                </div>
              </FieldCustom>
              <FieldText form={form} set={setF} k="priority" label="Öncelik" type="number" num />
              <FieldText form={form} set={setF} tried={tried} k="ruleName" label="Kural Adı" req span />
              <FieldText form={form} set={setF} k="description" label="Açıklama" span />
              <FieldCustom label="Kategori">
                <div className="dps-ff-input">
                  <select
                    value={form.categoryId ? String(form.categoryId) : ''}
                    onChange={(e) => {
                      const idv = e.target.value;
                      const name = categories.find((c) => String(c.id) === idv)?.name || '';
                      setF('categoryId', idv ? +idv : '');
                      setF('category', name);
                    }}
                  >
                    {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
                </div>
              </FieldCustom>
              <FieldText form={form} set={setF} k="targetEntity" label="Hedef Entity" />
              <FieldText form={form} set={setF} k="actionTemplate" label="Aksiyon Şablonu" placeholder="Örn: Rakip {competitor_name} dolabı %{shelf_space} ele geçirdi." span />
              <FieldText form={form} set={setF} k="suppressRuleIds" label='Baskılanan Kural ID&apos;leri (JSON array veya GUID listesi)' placeholder='["guid1","guid2"]' span />
              <FieldSelect form={form} set={setF} k="isActive" label="Aktiflik"
                options={[{ value: true, label: 'Aktif' }, { value: false, label: 'Pasif' }]} />
            </Grid>
          </Section>

          <Section icon="layers" title="Koşullar" subtitle="Alan / operatör / değer koşulları"
            right={<button className="dps-blk-add" onClick={addCondition}><Icon name="plus" size={15} /> Koşul Ekle</button>}>
            <Grid cols={2}>
              <FieldCustom label="Mantıksal Operatör">
                <div className="dps-ff-input">
                  <select value={definition.logicalOperator} onChange={(e) => setDefinition((d) => ({ ...d, logicalOperator: e.target.value }))}>
                    <option value="AND">AND (Tümü sağlanmalı)</option>
                    <option value="OR">OR (Herhangi biri sağlanmalı)</option>
                  </select>
                  <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
                </div>
              </FieldCustom>
            </Grid>

            {definition.conditions.length === 0 ? (
              <div className="dps-empty"><Icon name="layers" size={34} /><p>Henüz koşul yok</p><span>“Koşul Ekle” ile koşul ekleyin.</span></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr><th>Alan</th><th>Operatör</th><th>Değer</th><th className="dps-td-actions">İşlem</th></tr>
                </thead>
                <tbody>
                  {definition.conditions.map((cond, i) => {
                    const inputDefinition = getInputDefinitionForField(cond.field);
                    const operators = getOperatorsForDataType(inputDefinition?.dataTypeLabel);
                    const noValue = ['IsNull', 'IsNotNull'].includes(cond.operator);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="dps-ff-input">
                            {workflowInputs.length > 0 ? (
                              <select value={cond.field} onChange={(e) => updateConditionField(i, e.target.value)}>
                                <option value="">Alan seçiniz</option>
                                {workflowInputs.map((input) => (
                                  <option key={input.id} value={input.fieldName}>
                                    {input.fieldName}{input.isRequired ? ' *' : ''}{input.dataTypeLabel ? ` (${input.dataTypeLabel})` : ''}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input value={cond.field} placeholder="Alan adı" onChange={(e) => updateCondition(i, 'field', e.target.value)} />
                            )}
                            {workflowInputs.length > 0 && <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>}
                          </div>
                        </td>
                        <td>
                          <div className="dps-ff-input">
                            <select value={cond.operator} onChange={(e) => updateCondition(i, 'operator', e.target.value)}>
                              {operators.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                            </select>
                            <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
                          </div>
                        </td>
                        <td>
                          <div className="dps-ff-input">
                            <input
                              placeholder={inputDefinition?.dataTypeLabel === 'Date' ? 'Örn. 2026-04-05' : 'Değer'}
                              value={cond.value}
                              onChange={(e) => updateCondition(i, 'value', e.target.value)}
                              disabled={noValue}
                            />
                          </div>
                        </td>
                        <td className="dps-td-actions">
                          <button className="dps-row-btn danger" onClick={() => removeCondition(i)}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {workflowInputs.length === 0 && form.workflowName && (
              <div className="dps-note"><Icon name="info" size={16} /> Bu workflow için tanımlı input alanı yok. Alan adlarını elle girebilirsiniz.</div>
            )}

            <Grid cols={2}>
              <FieldCustom label="Başarı Mesajı" span>
                <div className="dps-ff-input">
                  <input value={definition.successEvent || ''} onChange={(e) => setDefinition((d) => ({ ...d, successEvent: e.target.value }))} />
                </div>
              </FieldCustom>
              <FieldCustom label="Hata Mesajı" span>
                <div className="dps-ff-input">
                  <input value={definition.errorMessage || ''} onChange={(e) => setDefinition((d) => ({ ...d, errorMessage: e.target.value }))} />
                </div>
              </FieldCustom>
            </Grid>
          </Section>

          <Section icon="wallet" title="Skorlama ve Ağırlıklar"
            subtitle={`Bileşik Skor ≈ ${computedScore.toFixed(2)}`}
            note={weightOk ? undefined : `Ağırlıkların toplamı 1.0 olmalı (şu an: ${weightSum.toFixed(3)}).`}>
            <div className="dps-grid2">
              {[
                { label: 'Etki', scoreKey: 'impactScore', weightKey: 'impactWeight' },
                { label: 'Kolaylık', scoreKey: 'easeScore', weightKey: 'easeWeight' },
                { label: 'Aciliyet', scoreKey: 'urgencyScore', weightKey: 'urgencyWeight' },
                { label: 'İlişki', scoreKey: 'relationScore', weightKey: 'relationWeight' },
              ].map(({ label, scoreKey, weightKey }) => (
                <div key={scoreKey} className="dps-ff">
                  <div className="dps-ff-lbl">{label}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="dps-ff-input" style={{ flex: 1 }}>
                      <input type="number" min={0} max={10} step={0.5} value={form[scoreKey]}
                        onChange={(e) => setF(scoreKey, parseFloat(e.target.value))} title="Puan (0-10)" />
                    </div>
                    <div className="dps-ff-input" style={{ flex: 1 }}>
                      <input type="number" min={0} max={1} step={0.05} value={form[weightKey]}
                        onChange={(e) => setF(weightKey, parseFloat(e.target.value))} title="Ağırlık (0-1)" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, fontWeight: 600, color: weightOk ? 'var(--text-secondary)' : '#dc3545' }}>
              Ağırlık toplamı: {weightSum.toFixed(3)} {weightOk ? '' : '— 1.0 olmalı'}
            </div>
          </Section>

          {!isNew && (
            <Section icon="briefcase" title="Güncelleme Notu" subtitle="Bu düzenleme için açıklama (versiyon geçmişine yazılır)">
              <Grid cols={2}>
                <FieldText form={form} set={setF} k="changeNote" label="Not" placeholder="Değişiklik sebebi..." span />
              </Grid>
            </Section>
          )}

          {jsonError && <p style={{ color: '#dc3545', margin: '0 4px' }}>{jsonError}</p>}
        </div>
      )}

      {activeTab === 'lifecycle' && !isNew && (
        <div className="dps-form">
          <Section icon="shield" title="Durum ve Yaşam Döngüsü" subtitle="Kuralın mevcut durumu ve uygulanabilir geçişler">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={{ color: si.color, fontWeight: 700 }}>● {si.label}</span>
              <span style={{ color: 'var(--text-secondary)' }}>Versiyon: v{version}</span>
              <span style={{ color: 'var(--text-secondary)' }}>Bileşik Skor: {computedScore.toFixed(2)}</span>
            </div>
            {lifecycleActions.length === 0 ? (
              <div className="dps-note"><Icon name="info" size={16} /> Bu durumda uygulanabilir bir geçiş yok.</div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {lifecycleActions.map((item) => (
                  <button key={item.action} className="dps-btn" onClick={() => openNoteModal(item.action)}>
                    <Icon name={item.icon} size={16} /> {item.label}
                  </button>
                ))}
              </div>
            )}
          </Section>

          <Section icon="layers" title="Versiyon Geçmişi" subtitle="Durum değişikliği ve düzenleme kayıtları">
            {historyLoading ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : history.length === 0 ? (
              <div className="dps-empty"><Icon name="layers" size={34} /><p>Kayıt yok</p></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr><th>Versiyon</th><th>Durum</th><th>Değiştiren</th><th>Tarih</th><th>Not</th></tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td>v{item.version}</td>
                      <td>{item.status}</td>
                      <td>{item.changedBy}</td>
                      <td>{item.changedDate ? new Date(item.changedDate).toLocaleString('tr-TR') : '—'}</td>
                      <td>{item.changeNote || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      {/* Yaşam döngüsü not modalı */}
      {noteModal && (
        <SubModal
          title={ACTION_TITLES[noteModal.action]}
          maxWidth={480}
          onClose={() => setNoteModal(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setNoteModal(null)} disabled={actionLoading}>İptal</button>
              <button className="dps-btn primary" onClick={confirmAction} disabled={actionLoading}>{actionLoading ? 'İşleniyor…' : 'Onayla'}</button>
            </>
          )}
        >
          <FieldCustom label="Not (opsiyonel)">
            <div className="dps-ff-input area">
              <textarea rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Geçiş sebebini yazabilirsiniz..." />
            </div>
          </FieldCustom>
        </SubModal>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Kuralı Sil"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.ruleName}”</strong> kuralını kalıcı olarak silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      {/* Workflow test paneli (mevcut overlay bileşeni) */}
      {showTest && (
        <RuleTestPanel
          workflowName={form.workflowName}
          tenantId={tenantId}
          onClose={() => setShowTest(false)}
        />
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default RuleDetailPage;
